import { useEffect } from "react";
import type { Cotizacion } from "../domain/types";
import type { Propuesta } from "../domain/propuestas";
import { esSnapshot } from "../domain/snapshot";
import { useEditor } from "./editor";

// Autoguardado del borrador en el navegador (capacitación 03-oct-2026): dentro
// del CRM el cotizador se recarga al cambiar de pantalla y se perdía todo lo
// que no se había guardado (Karla, Fernando). Mientras haya cambios sin
// guardar, el editor se copia aquí; al volver a entrar se ofrece recuperarlo.
// Es una conveniencia por navegador y por vendedor, no reemplaza "Guardar":
// el historial sigue siendo lo que se guarda en el servidor.
//
// Un borrador por pestaña (clave con el id de esta carga de la página): dos
// pestañas no se pisan, y cada una solo borra el suyo. El de otra carga solo
// se borra si el vendedor elige "Descartar" (cerrar el diálogo lo deja para la
// próxima vez). Revisión de Codex, 05-oct-2026.

const VERSION = 1;
const VIGENCIA_MS = 14 * 24 * 3600_000;
const ESPERA_MS = 800;
const PREFIJO = "cotizador_borrador_";
const ESTA_CARGA =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : String(Date.now());
const clave = (vendedorId: string, carga: string) =>
  `${PREFIJO}${vendedorId}_${carga}`;

export interface Borrador {
  clave: string;
  guardadoEn: number;
  cotizacion: Cotizacion;
  propuestas: Propuesta[];
  siguientePropuesta: number;
}

function esPropuesta(p: unknown): p is Propuesta {
  const r = p as Record<string, unknown> | null;
  return (
    !!r &&
    typeof r.numero === "string" &&
    typeof r.etiqueta === "string" &&
    Array.isArray(r.sitios)
  );
}

function quitar(k: string) {
  try {
    localStorage.removeItem(k);
  } catch {
    // sin almacenamiento: nada que borrar
  }
}

// El borrador más reciente de este vendedor que dejó OTRA carga de la página
// (una recarga, otra pestaña cerrada). Limpia los vencidos o inválidos.
export function leerBorrador(
  vendedorId: string,
  ahora = Date.now(),
): Borrador | null {
  let mejor: Borrador | null = null;
  try {
    const prefijo = `${PREFIJO}${vendedorId}_`;
    const claves: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefijo) && k !== clave(vendedorId, ESTA_CARGA))
        claves.push(k);
    }
    for (const k of claves) {
      let b: Record<string, unknown> | null = null;
      try {
        b = JSON.parse(localStorage.getItem(k) || "null");
      } catch {
        b = null;
      }
      if (
        !b ||
        b.v !== VERSION ||
        typeof b.guardadoEn !== "number" ||
        ahora - b.guardadoEn > VIGENCIA_MS ||
        !esSnapshot(b.cotizacion) ||
        !Array.isArray(b.propuestas) ||
        !b.propuestas.every(esPropuesta) ||
        typeof b.siguientePropuesta !== "number"
      ) {
        quitar(k);
        continue;
      }
      if (!mejor || b.guardadoEn > mejor.guardadoEn)
        mejor = {
          clave: k,
          guardadoEn: b.guardadoEn,
          cotizacion: b.cotizacion as Cotizacion,
          propuestas: b.propuestas as Propuesta[],
          siguientePropuesta: b.siguientePropuesta,
        };
    }
  } catch {
    return null;
  }
  return mejor;
}

// "Descartar": solo ese borrador.
export const borrarBorrador = (b: Borrador) => quitar(b.clave);

// Copia el editor de ESTA carga; sin cambios pendientes, borra la copia de
// esta carga (lo guardado ya está en Mis cotizaciones).
// Devuelve si quedó escrito (false: almacenamiento lleno o bloqueado).
export function guardarBorrador(
  vendedorId: string,
  ahora = Date.now(),
): boolean {
  const s = useEditor.getState();
  const k = clave(vendedorId, ESTA_CARGA);
  if (!s.dirty) {
    quitar(k);
    return true;
  }
  try {
    localStorage.setItem(
      k,
      JSON.stringify({
        v: VERSION,
        guardadoEn: ahora,
        cotizacion: s.cotizacion,
        propuestas: s.propuestas,
        siguientePropuesta: s.siguientePropuesta,
      }),
    );
    return true;
  } catch {
    // Almacenamiento lleno o bloqueado (p. ej. iframe sin permiso): el
    // borrador simplemente no se guarda; "Guardar" sigue funcionando.
    return false;
  }
}

// Recuperar: el borrador pasa a ser el trabajo de esta carga (se copiará con
// la clave de esta carga) y el de la carga anterior se retira.
export function restaurarBorrador(b: Borrador, vendedorId: string) {
  useEditor.setState((s) => ({
    cotizacion: b.cotizacion,
    propuestas: b.propuestas,
    siguientePropuesta: b.siguientePropuesta,
    dirty: true,
    revisarPiezas: false,
    cargas: s.cargas + 1,
    preciosActualizados: [],
    operacionGuardado: null,
    clavesPropuestas: {},
  }));
  // El de la carga anterior se retira solo si la copia quedó escrita: si
  // no, sigue siendo la única copia del trabajo.
  if (guardarBorrador(vendedorId)) quitar(b.clave);
}

// Copia el editor mientras haya cambios sin guardar: con una pequeña espera
// para no escribir en cada tecla, pero el borrado (al guardar o cargar otra)
// es inmediato. Al salir de la página, o si cambia el vendedor (sesión
// vencida, otro usuario), lo pendiente se escribe antes de soltar.
export function useAutoguardado(vendedorId: string | undefined) {
  useEffect(() => {
    if (!vendedorId) return;
    let temporizador: ReturnType<typeof setTimeout> | undefined;
    const ahora = () => {
      clearTimeout(temporizador);
      temporizador = undefined;
      guardarBorrador(vendedorId);
    };
    const desuscribir = useEditor.subscribe((s, antes) => {
      if (
        s.cotizacion === antes.cotizacion &&
        s.propuestas === antes.propuestas &&
        s.dirty === antes.dirty
      )
        return;
      if (!s.dirty) return ahora();
      clearTimeout(temporizador);
      temporizador = setTimeout(ahora, ESPERA_MS);
    });
    // Al volver a entrar con trabajo en pantalla (sesión vencida), copiarlo ya.
    if (useEditor.getState().dirty) ahora();
    window.addEventListener("pagehide", ahora);
    const alOcultar = () => {
      if (document.visibilityState === "hidden" && temporizador) ahora();
    };
    document.addEventListener("visibilitychange", alOcultar);
    return () => {
      if (temporizador) ahora();
      desuscribir();
      window.removeEventListener("pagehide", ahora);
      document.removeEventListener("visibilitychange", alOcultar);
    };
  }, [vendedorId]);
}
